import { html, LitElement, nothing, TemplateResult } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import {
  fetchDeliveriesContext,
  fetchOrdersContext,
  fetchProductSearchContext,
  fetchProductsOfOrderContext,
  searchDeliveriesContext,
  searchOrdersContext,
  searchProductsContext,
} from 'components/ordering/rma/components/context/rma-context.js'
import { RmaOrderDTO } from 'components/ordering/rma/api/dtos/rma-order-dto.js'
import { RmaProductDTO } from 'components/ordering/rma/api/dtos/rma-product-dto.js'
import 'components/ordering/rma/components/product-row/product-row.js'
import { rmaSearch } from './search.style.js'

type SearchPanel = 'orders' | 'deliveries' | 'products'

// The products of a matched order need a call of their own — the search
// endpoint only returns them for a specific shipment and sale. Load them up
// front for the first few hits so the results read as a product list, and leave
// the rest to expand on demand rather than firing a request per result.
const AUTO_LOADED_ORDERS = 5

type SearchTabContent = TemplateResult | typeof nothing

type SearchFetch = (searchTerm: string) => Promise<void>

type FetchOrderProducts = (shipmentId: string, saleId: string) => Promise<RmaProductDTO[]>

type SearchTab = {
  panel: SearchPanel
  label: ReturnType<typeof t>
  placeholder: ReturnType<typeof t>
  selected: boolean
}

@customElement('csc-rma-search')
export class CSCRmaSearch extends LitElement {
  static override styles = [hostStyle, rmaSearch]

  @consume({ context: fetchOrdersContext, subscribe: true })
  private fetchOrders: SearchFetch | null = null

  @consume({ context: fetchDeliveriesContext, subscribe: true })
  private fetchDeliveries: SearchFetch | null = null

  @consume({ context: fetchProductSearchContext, subscribe: true })
  private fetchProductSearch: SearchFetch | null = null

  @consume({ context: fetchProductsOfOrderContext, subscribe: true })
  private fetchOrderProducts: FetchOrderProducts | null = null

  @consume({ context: searchOrdersContext, subscribe: true })
  private searchOrders: RmaOrderDTO[] = []

  @consume({ context: searchDeliveriesContext, subscribe: true })
  private searchDeliveries: RmaOrderDTO[] = []

  @consume({ context: searchProductsContext, subscribe: true })
  private searchProducts: RmaProductDTO[] = []

  @state()
  private terms: Record<SearchPanel, string> = { orders: '', deliveries: '', products: '' }

  @state()
  private searchedPanel: SearchPanel | null = null

  @state()
  private searching: Record<SearchPanel, boolean> = { orders: false, deliveries: false, products: false }

  @state()
  private productsByOrder: Map<string, RmaProductDTO[]> = new Map()

  private onTermInput(panel: SearchPanel, event: Event): void {
    this.terms = { ...this.terms, [panel]: (event.target as HTMLInputElement).value }
  }

  private async runSearch(panel: SearchPanel): Promise<void> {
    const searchTerm = this.terms[panel]
    if (!searchTerm) return
    this.searching = { ...this.searching, [panel]: true }
    try {
      this.productsByOrder = new Map()
      await this.fetchFor(panel, searchTerm)
      // Only mark the panel as searched once its products are in, so the empty
      // state can't flash between the two calls.
      await this.loadProductsOfFirstOrders(panel)
      this.searchedPanel = panel
    } finally {
      this.searching = { ...this.searching, [panel]: false }
    }
  }

  private async loadProductsOfFirstOrders(panel: SearchPanel): Promise<void> {
    if (panel === 'products') return
    const orders = this.resultsFor(panel) as RmaOrderDTO[]
    const loaded = await Promise.all(
      orders.slice(0, AUTO_LOADED_ORDERS).map(async (order: RmaOrderDTO) => {
        const products = (await this.fetchOrderProducts?.(order.shipmentId ?? '', order.saleId)) ?? []
        return [this.orderKey(order), products] as const
      })
    )
    const next = new Map(this.productsByOrder)
    loaded.forEach(([key, products]) => next.set(key, products))
    this.productsByOrder = next
  }

  private async fetchFor(panel: SearchPanel, searchTerm: string): Promise<void> {
    if (panel === 'orders') return this.fetchOrders?.(searchTerm)
    if (panel === 'deliveries') return this.fetchDeliveries?.(searchTerm)
    return this.fetchProductSearch?.(searchTerm)
  }

  private orderKey(order: RmaOrderDTO): string {
    return `${order.shipmentId ?? ''}:${order.saleId}`
  }

  private async onOrderToggle(order: RmaOrderDTO, event: Event): Promise<void> {
    const opened = (event.target as unknown as { open: boolean }).open
    const key = this.orderKey(order)
    if (!opened || this.productsByOrder.has(key)) return
    const products = (await this.fetchOrderProducts?.(order.shipmentId ?? '', order.saleId)) ?? []
    this.productsByOrder = new Map(this.productsByOrder).set(key, products)
  }

  private resultsFor(panel: SearchPanel): RmaOrderDTO[] | RmaProductDTO[] {
    if (panel === 'orders') return this.searchOrders
    if (panel === 'deliveries') return this.searchDeliveries
    return this.searchProducts
  }

  // Matching orders can still leave nothing on screen when none of their
  // products can be returned, so the empty state follows what is rendered.
  private hasVisibleResults(panel: SearchPanel): boolean {
    if (panel === 'products') return this.searchProducts.length > 0
    const orders = this.resultsFor(panel) as RmaOrderDTO[]
    if (orders.length > AUTO_LOADED_ORDERS) return true
    return orders
      .slice(0, AUTO_LOADED_ORDERS)
      .some((order: RmaOrderDTO) => (this.productsByOrder.get(this.orderKey(order))?.length ?? 0) > 0)
  }

  private renderNoResults(panel: SearchPanel): SearchTabContent {
    if (this.searchedPanel !== panel || this.hasVisibleResults(panel)) return nothing
    return html`
      <cse-alert variant="warning">
        <span slot="description">
          ${t('no_results_found', { ns: 'csc-rma-search', default: 'No results found' })}
        </span>
      </cse-alert>
    `
  }

  private renderResults(panel: SearchPanel): SearchTabContent {
    if (this.searchedPanel !== panel) return nothing
    if (panel === 'products') {
      return html`${this.searchProducts.map((product: RmaProductDTO) => this.renderProductRow(product))}`
    }
    const orders = panel === 'orders' ? this.searchOrders : this.searchDeliveries
    return html`${orders.map((order: RmaOrderDTO, index: number) =>
      index < AUTO_LOADED_ORDERS ? this.renderOrderProductRows(order) : this.renderOrderRow(order)
    )}`
  }

  // A pre-loaded order shows its products directly, the way the design lists
  // search results.
  private renderOrderProductRows(order: RmaOrderDTO): SearchTabContent {
    const products = this.productsByOrder.get(this.orderKey(order))
    if (products === undefined) return nothing
    if (products.length === 0) return nothing
    return html`${products.map((product: RmaProductDTO) => this.renderProductRow(product))}`
  }

  private renderOrderRow(order: RmaOrderDTO): TemplateResult {
    const reference = order.erpOrderNumber ?? order.saleReference
    const products = this.productsByOrder.get(this.orderKey(order))
    return html`
      <cse-accordion class="rma-search__order" @accordion-toggle=${(event: Event) => this.onOrderToggle(order, event)}>
        <div slot="title" class="rma-search__order-title">
          <cse-text variant="body-m">${reference}</cse-text>
          ${order.shipmentReference
            ? html`<cse-text variant="caption-m">${order.shipmentReference}</cse-text>`
            : nothing}
        </div>
        <div slot="content" class="rma-search__order-products">${this.renderOrderProducts(products)}</div>
      </cse-accordion>
    `
  }

  private renderOrderProducts(products: RmaProductDTO[] | undefined): SearchTabContent {
    if (products === undefined) return nothing
    if (products.length === 0) {
      return html`<cse-text variant="caption-m">
        ${t('no_products_in_order', { ns: 'csc-rma-search', default: 'No returnable products in this order' })}
      </cse-text>`
    }
    return html`${products.map((product: RmaProductDTO) => this.renderProductRow(product))}`
  }

  private renderProductRow(product: RmaProductDTO): TemplateResult {
    return html`<csc-rma-product-row .productData=${product}></csc-rma-product-row>`
  }

  private renderTab({ panel, label, placeholder, selected }: SearchTab): TemplateResult {
    return html`
      <cse-tab panel="${panel}" ?selected=${selected}>${label}</cse-tab>
      <cse-tab-panel name="${panel}" slot="content" ?selected=${selected}>
        <div class="rma-search__panel">
          <div class="rma-search__bar">
            <cse-text-input
              class="rma-search__input"
              type="text"
              clearable
              placeholder=${placeholder}
              .value=${this.terms[panel]}
              @input=${(event: Event) => this.onTermInput(panel, event)}
              @change=${() => this.runSearch(panel)}
            ></cse-text-input>
            <cse-button variant="primary" size="small" ?loading=${this.searching[panel]} @click=${() => this.runSearch(panel)}>
              ${t('search', { ns: 'csc-rma-search', default: 'Search' })}
            </cse-button>
          </div>
          ${this.searchedPanel === panel
            ? html`<div class="rma-search__results">
                ${this.renderResults(panel)}
                ${this.renderNoResults(panel)}
              </div>`
            : nothing}
        </div>
      </cse-tab-panel>
    `
  }

  private get tabs(): SearchTab[] {
    return [
      {
        panel: 'orders',
        label: t('tab_order_number', { ns: 'csc-rma-search', default: 'Order number' }),
        placeholder: t('order_search_placeholder', { ns: 'csc-rma-search', default: 'Search for an order number' }),
        selected: true,
      },
      {
        panel: 'deliveries',
        label: t('tab_reference', { ns: 'csc-rma-search', default: 'Reference' }),
        placeholder: t('reference_search_placeholder', { ns: 'csc-rma-search', default: 'Search for a reference' }),
        selected: false,
      },
      {
        panel: 'products',
        label: t('tab_product_code', { ns: 'csc-rma-search', default: 'Product code' }),
        placeholder: t('product_search_placeholder', { ns: 'csc-rma-search', default: 'Search for a product code' }),
        selected: false,
      },
    ]
  }

  override render(): TemplateResult {
    return html`
      <cse-paper variant="secondary" size="extraLarge" class="rma-search">
        <div class="rma-search__text">
          <cse-text as="h2" variant="heading-2" class="rma-search__heading">
            ${t('search_heading', { ns: 'csc-rma-search', default: 'Is your order not listed?' })}
          </cse-text>
          <cse-text variant="body-m" class="rma-search__intro">
            ${t('search_intro', { ns: 'csc-rma-search', default: 'Search for your order, reference or product.' })}
          </cse-text>
        </div>
        <cse-tabs>${this.tabs.map((tab: SearchTab) => this.renderTab(tab))}</cse-tabs>
      </cse-paper>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-rma-search': CSCRmaSearch
  }
}
